import type { DayMaskDefinition, EnabledDateRange, InsertBehavior, UTCPeriodHrMins } from '@iotv/iotv-v3-types'
import { dayMaskAllTrueTemplate, isAscUTCPeriodHrsMinsArr, isDayMaskDefinition, isUTCPeriodMins } from '@iotv/iotv-v3-types'
import { v4 } from 'uuid'
import type { UserAttributeDefs } from '../../..'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../../../util/lambda'
import { insertUTCPeriodHrMinsIntoArray, utcPeriodHrMinToDateRanges } from '../../../util/time/UTCPeriods'
import { ModelCommon } from '../../ModelCommon'
import { enumerable } from '../enumerableDecorator'
import { EntityProgrammeConstructorProps, IEntityProgramme, IEntityProgrammeFunctions, isEntityProgramme, supportedTypes } from './types'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

export class EntityProgramme extends ModelCommon implements IEntityProgramme {
  constructor({ id = v4(), supportedType, enabled, maxNumberOfEntitiesPerTimeslot, name, timeslots, dayMask, ...others }: EntityProgrammeConstructorProps) {
    super('EntityProgramme', { id, ...others })
    this.maxNumberOfEntitiesPerTimeslot = maxNumberOfEntitiesPerTimeslot ?? null
    this.name = name
    this.supportedType = supportedType
    this.enabled = enabled ?? true
    if (dayMask) {
      if (isDayMaskDefinition(dayMask)) {
        this.dayMask = dayMask
      } else {
        throw new Error(`EntityProgramme received invalid dayMask`)
      }
    } else {
      this.dayMask = dayMaskAllTrueTemplate
    }
    if (timeslots) {
      if (timeslots.every(isUTCPeriodMins)) {
        this.timeslots = timeslots ?? []
      } else {
        throw new Error(`EntityProgramme received invalid timeslots`)
      }
    } else {
      this.timeslots = []
    }
  }
  supportedType: string
  enabled: boolean
  maxNumberOfEntitiesPerTimeslot: number | null
  name: string
  dayMask: DayMaskDefinition
  timeslots: UTCPeriodHrMins[]

  getUserAttributes(): UserAttributeDefs {
    return {
      ...super.getUserAttributes(),
      supportedType: {
        type: 'Enum',
        editable: false,
        required: true,
        enumKv: supportedTypes,
      },
      maxNumberOfEntitiesPerTimeslot: {
        type: 'Number',
        default: null,
        editable: false,
        label: `Max Entities Per Slot`,
        precision: 0,
        required: false,
        description: 'the maximum number of entities allowed for a slot.',
      },
    }
  }

  @enumerable(false)
  getTimeslotsLength() {
    return this.timeslots.length
  }

  @enumerable(false)
  pushTimeslot(timeslot: UTCPeriodHrMins) {
    this.timeslots.push(timeslot)
    return this
  }

  @enumerable(false)
  popTimeslot() {
    return this.timeslots.pop()
  }

  // TODO delete slot, and leave gap - this requires manipulation of edges which is not ready yet - this is now done at EntityScheduler level
  @enumerable(false)
  insertTimeslot(newTimeslot: UTCPeriodHrMins, insertBehavior: InsertBehavior) {
    insertUTCPeriodHrMinsIntoArray({ newTimeslot, timeslots: this.timeslots, insertBehavior })
    return this
  }

  @enumerable(false)
  isValid() {
    const isClassInstance = isEntityProgramme(this)
    const isValidArr = isAscUTCPeriodHrsMinsArr(this.timeslots)
    debug && console.log({ isClassInstance, isValidArr })
    if (!isValidArr) {
      debug && console.log(`Invalid timeslots`, this.timeslots)
    }
    const isValid = isClassInstance && isValidArr
    if (!isValid) {
      console.log(`EntityProgramme isValid returning false with`, { isClassInstance, isValidArr })
    }
    return isValid
  }

  @enumerable(false)
  timeslotsToDateRanges: IEntityProgrammeFunctions['timeslotsToDateRanges'] = ({ relativeDate }) => {
    const unorderedDateRanges = this.timeslots.reduce((a, c) => {
      a.push(
        ...utcPeriodHrMinToDateRanges({ relativeDate, utcPeriodHrMins: c, dayMask: this.dayMask }).map((dr) => {
          return { enabled: this.enabled, dateRange: dr }
        }),
      )
      return a
    }, [] as EnabledDateRange[])

    let sortedDateRanges: EnabledDateRange[] = unorderedDateRanges
    if (unorderedDateRanges.length > 1) {
      sortedDateRanges = unorderedDateRanges.sort((a, b) => (a.dateRange.at(0)! < b.dateRange.at(0)! ? -1 : 1))
    }
    return sortedDateRanges
  }

  @enumerable(false)
  clone() {
    const { supportedType, dayMask: originalDayMask, timeslots: originalTimeSlots, maxNumberOfEntitiesPerTimeslot, name: originalName } = this
    const name = `${originalName} clone`
    const dayMask: DayMaskDefinition = Object.assign({}, originalDayMask)
    const timeslots = originalTimeSlots.map((timeslot) => Object.assign({}, timeslot))
    const enabled = this.enabled
    return new EntityProgramme({ id: v4(), enabled, supportedType, maxNumberOfEntitiesPerTimeslot, dayMask, name, timeslots })
  }
}
